import * as React from 'react';

import { addDataSource } from 'src/actions/data-sources/action-creators/addDataSource';
import { refreshDataSources } from 'src/actions/data-sources/action-creators/refreshDataSources';
import { refreshDataSourceTables } from 'src/actions/data-sources/action-creators/refreshDataSourceTables';
import { dataSourcesStardogRequestDispatchers } from 'src/actions/data-sources/actionCreators';
import { DataSourceTypeGrid } from 'src/components/dataSourceSelection/DataSourceTypeGrid';
import { useDataSourceTypeList } from 'src/components/dataSourceSelection/hooks/useDataSourceTypeList';
import { SelectionPanelContainer } from 'src/components/dataSourceSelection/SelectionPanelContainer';
import { PanelProps } from 'src/components/panelStack/PanelStack';
import {
  DataSourceOption,
  DataSourceType,
  SAMPLE_MUSIC_DATA_SOURCE_TYPE,
} from 'src/constants/dataSourceTypeDescription';
import { SAMPLE_MUSIC_DATA_SOURCE } from 'src/constants/sampleDataSource';
import { useProjectVirtualGraphPreviewDialog } from 'src/containers/ProjectVirtualGraphPreviewDialog/context/useProjectVirtualGraphPreviewDialog';
import { ConnectionSelectionPanel } from 'src/containers/ProjectVirtualGraphPreviewDialog/panels/ConnectionSelectionPanel';
import { DataSourceCreationPanel } from 'src/containers/ProjectVirtualGraphPreviewDialog/panels/DataSourceCreationPanel';
import { connectionSelector } from 'src/selectors/connection';
import { dataSourcesByTypeSelector } from 'src/selectors/dataSourcesByType';
import { isConnectingSelector } from 'src/selectors/isConnecting';
import { isRefreshingDataSourcesSelector } from 'src/selectors/isRefreshingDataSources';
import * as copy from 'src/templates/copy';
import { useDialogPanelSetup } from 'src/utils/hooks/useDialogPanelSetup';
import { useReduxDispatch } from 'src/utils/hooks/useReduxDispatch';
import { useReduxSelector } from 'src/utils/hooks/useReduxSelector';

export const DataSourceTypeSelectionPanel: React.VFC<PanelProps> = ({
  closePanel,
  openPanel,
}) => {
  const dispatch = useReduxDispatch();
  const {
    setDialogTitle,
    setHeaderBackButton,
    setFullSize,
    setFooterOptions,
    localVirtualGraph,
    updateVirtualGraphDataSource,
    hasRefreshedDataSources,
    setHasRefreshedDataSources,
  } = useProjectVirtualGraphPreviewDialog();

  const connection = useReduxSelector(connectionSelector);
  const dataSourcesByType = useReduxSelector(dataSourcesByTypeSelector);
  const isConnecting = useReduxSelector(isConnectingSelector);
  const isRefreshing = useReduxSelector(isRefreshingDataSourcesSelector);

  const typeList = useDataSourceTypeList();

  const [isSampleLoading, setIsSampleLoading] = React.useState(false);

  // Refresh data sources when panel mounts, but only once per dialog session.
  // This prevents wiping data when the panel re-mounts during closePanel() sequence.
  React.useEffect(() => {
    if (!hasRefreshedDataSources) {
      setHasRefreshedDataSources(true);
      dispatch(refreshDataSources());
    }
  }, [dispatch, hasRefreshedDataSources, setHasRefreshedDataSources]);

  const isLoading = isConnecting || isRefreshing;
  const isLoadingTypes = typeList.length === 0 && isLoading;

  const handleBack = React.useCallback(() => {
    closePanel?.();
  }, [closePanel]);

  const handleSelectDataSourceType = React.useCallback(
    async (dataSourceType: DataSourceType) => {
      // Handle sample music data source specially - auto-create and select it
      if (
        localVirtualGraph &&
        connection &&
        dataSourceType === SAMPLE_MUSIC_DATA_SOURCE_TYPE
      ) {
        const existingDataSource = dataSourcesByType[
          SAMPLE_MUSIC_DATA_SOURCE.type
        ]?.find(({ id }) => id === SAMPLE_MUSIC_DATA_SOURCE.id);

        setIsSampleLoading(true);
        if (existingDataSource) {
          await dispatch(refreshDataSourceTables(SAMPLE_MUSIC_DATA_SOURCE.id));
        } else {
          const didSucceed = await dispatch(
            addDataSource(SAMPLE_MUSIC_DATA_SOURCE)
          );
          if (!didSucceed) {
            setIsSampleLoading(false);
            return;
          }
        }
        setIsSampleLoading(false);
        updateVirtualGraphDataSource({
          connectionId: connection.id || null,
          dataSourceEndpoint:
            SAMPLE_MUSIC_DATA_SOURCE.options[DataSourceOption.ENDPOINT],
          dataSourceId: SAMPLE_MUSIC_DATA_SOURCE.id,
          dataSourceType: SAMPLE_MUSIC_DATA_SOURCE.type,
        });
        closePanel?.();
        return;
      }

      const dataSources = dataSourcesByType[dataSourceType] || [];

      // If no existing connections, skip directly to creation panel
      if (dataSources.length === 0) {
        openPanel?.({
          renderPanel: DataSourceCreationPanel,
          props: { dataSourceType },
        });
        return;
      }

      // Request options for any data sources that don't have them yet
      if (connection) {
        dataSources.forEach((dataSource) => {
          if (!dataSource.options) {
            dataSourcesStardogRequestDispatchers.requestDataSourceOptions(
              dataSource.id,
              connection
            );
          }
        });
      }

      openPanel?.({
        renderPanel: ConnectionSelectionPanel,
        props: { dataSourceType },
      });
    },
    [
      closePanel,
      connection,
      dataSourcesByType,
      dispatch,
      localVirtualGraph,
      openPanel,
      updateVirtualGraphDataSource,
    ]
  );

  useDialogPanelSetup(
    { setDialogTitle, setHeaderBackButton, setFullSize, setFooterOptions },
    {
      title:
        copy.components.virtualGraphPreviewDialog.dataSourceTypeSelection.title,
      fullSize: false,
      onBack: handleBack,
    }
  );

  return (
    <SelectionPanelContainer
      emptyMessage={
        copy.components.projectResourceDialog.dataSourceTypeSelectionPanel
          .noDataSources
      }
      isEmpty={!isLoadingTypes && typeList.length === 0}
      isLoading={isLoadingTypes}
      noGridWrapper
      subtitle={
        copy.components.virtualGraphPreviewDialog.dataSourceTypeSelection
          .subtitle
      }
    >
      <DataSourceTypeGrid
        dataSourceTypes={typeList}
        isSampleLoading={isSampleLoading}
        onSelectType={handleSelectDataSourceType}
      />
    </SelectionPanelContainer>
  );
};
